/**
 * VVS GSAP Hooks
 * ──────────────
 * Custom hooks for GSAP animations with proper cleanup.
 */

import { useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { VVS_MOTION, prefersReducedMotion, getIntensity } from './tokens';

// Register plugins
gsap.registerPlugin(ScrollTrigger);

// Use layout effect on client, effect on server
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Main GSAP hook with automatic cleanup
 */
export function useGSAPContext(callback, deps = []) {
  const contextRef = useRef(null);
  
  useIsomorphicLayoutEffect(() => {
    if (prefersReducedMotion()) return;
    
    // Create GSAP context for cleanup
    contextRef.current = gsap.context(() => {
      callback();
    });
    
    return () => {
      contextRef.current?.revert();
    };
  }, deps);
  
  return contextRef;
}

/**
 * Scroll-triggered reveal animation
 */
export function useScrollRevealGSAP(options = {}) {
  const ref = useRef(null);
  
  const {
    y = 60,
    opacity = 0,
    duration = VVS_MOTION.normal,
    ease = VVS_MOTION.enter,
    start = VVS_MOTION.triggerStart,
    delay = 0,
    stagger = 0,
    childSelector = null,
  } = options;
  
  useIsomorphicLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return;
    
    const intensity = getIntensity();
    const targets = childSelector 
      ? ref.current.querySelectorAll(childSelector) 
      : ref.current;
    
    const ctx = gsap.context(() => {
      gsap.from(targets, {
        y: y * intensity,
        opacity,
        duration,
        ease,
        delay,
        stagger: stagger * intensity,
        scrollTrigger: {
          trigger: ref.current,
          start,
          toggleActions: "play none none reverse",
        },
      });
    }, ref);
    
    return () => ctx.revert();
  }, [y, opacity, duration, ease, start, delay, stagger, childSelector]);
  
  return ref;
}

/**
 * Parallax effect on scroll
 */
export function useParallax(options = {}) {
  const ref = useRef(null);
  
  const {
    speed = VVS_MOTION.parallax.midground,
    direction = 'y',
  } = options;
  
  useIsomorphicLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return;
    
    const intensity = getIntensity();
    const distance = 100 * speed * intensity;
    
    const ctx = gsap.context(() => {
      gsap.to(ref.current, {
        [direction]: -distance,
        ease: "none",
        scrollTrigger: {
          trigger: ref.current,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    }, ref);
    
    return () => ctx.revert();
  }, [speed, direction]);
  
  return ref;
}

/**
 * Mouse tracking with magnetic effect
 */
export function useMouseTracker() {
  const mouseRef = useRef({ x: 0, y: 0, targetX: 0, targetY: 0 });
  const rafRef = useRef(null);
  
  useEffect(() => {
    if (prefersReducedMotion()) return;
    
    const handleMouseMove = (e) => {
      mouseRef.current.x = (e.clientX / window.innerWidth - 0.5) * 2;
      mouseRef.current.y = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    
    const tick = () => {
      const mouse = mouseRef.current;
      mouse.targetX += (mouse.x - mouse.targetX) * 0.05;
      mouse.targetY += (mouse.y - mouse.targetY) * 0.05;
      rafRef.current = requestAnimationFrame(tick);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    tick();
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);
  
  return mouseRef;
}

/**
 * Scroll velocity tracker
 */
export function useScrollVelocity(callback) {
  const velocityRef = useRef(0);
  
  useEffect(() => {
    if (prefersReducedMotion()) return;
    
    let lastScrollY = window.scrollY;
    let lastTime = Date.now();
    let rafId;
    
    const updateVelocity = () => {
      const currentScrollY = window.scrollY;
      const currentTime = Date.now();
      const deltaTime = currentTime - lastTime;
      
      if (deltaTime > 0) {
        const deltaScroll = currentScrollY - lastScrollY;
        velocityRef.current = Math.abs(deltaScroll / deltaTime) * 1000;
        
        // Normalize to 0-1 range
        const normalizedVelocity = Math.min(velocityRef.current / 3000, 1);
        callback?.(normalizedVelocity, velocityRef.current);
      }
      
      lastScrollY = currentScrollY;
      lastTime = currentTime;
      rafId = requestAnimationFrame(updateVelocity);
    };
    
    rafId = requestAnimationFrame(updateVelocity);
    
    return () => cancelAnimationFrame(rafId);
  }, [callback]);
  
  return velocityRef;
}

/**
 * Entrance animation for hero sections
 */
export function useHeroEntrance(elements = {}) {
  const containerRef = useRef(null);
  
  useIsomorphicLayoutEffect(() => {
    if (!containerRef.current || prefersReducedMotion()) return;
    
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: { ease: VVS_MOTION.wave },
      });
      
      // Stagger entrance of hero elements
      if (elements.eyebrow) {
        tl.from(elements.eyebrow, {
          y: 20,
          opacity: 0,
          duration: VVS_MOTION.normal,
        }, 0.2);
      }
      
      if (elements.title) {
        tl.from(elements.title, {
          y: 60,
          opacity: 0,
          duration: VVS_MOTION.slow,
        }, "-=0.3");
      }
      
      if (elements.subtitle) {
        tl.from(elements.subtitle, {
          y: 30,
          opacity: 0,
          duration: VVS_MOTION.normal,
        }, "-=0.5");
      }
      
      if (elements.cta) {
        tl.from(elements.cta, {
          scale: 0.95,
          opacity: 0,
          duration: VVS_MOTION.fast,
          stagger: VVS_MOTION.staggerFast,
        }, "-=0.3");
      }
    }, containerRef);
    
    return () => ctx.revert();
  }, [elements]);
  
  return containerRef;
}

export default {
  useGSAPContext,
  useScrollRevealGSAP,
  useParallax,
  useMouseTracker,
  useScrollVelocity,
  useHeroEntrance,
};
